/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Trans } from '@lingui/macro'
import RotateGif from 'assets/images/rotate.gif'
import FullScreenWhite from 'assets/svg/fullscreen-white-ico.svg'
import BigNumber from 'bignumber.js'
import { ButtonPrimary } from 'components/Button'
import { ROULETTE_SUBGRAPH_URL } from 'constants/subgraph'
import request, { gql } from 'graphql-request'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useRouletteContract } from 'hooks/useContract'
import usePlayerData from 'hooks/usePlayerData'
import { useCallback, useEffect, useState } from 'react'
import Unity, { UnityContext } from 'react-unity-webgl'
import { useCurrentRoute } from 'state/application/hooks'
import { useUserLocaleManager } from 'state/user/hooks'
import { useDarkModeManager } from 'state/user/hooks'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'
import useWindowOrientation from 'use-window-orientation'
import { DEFAULT_TOKEN_DECIMALS } from 'utils/bigNumber'
import { getBalanceNumber } from 'utils/formatBalance'
import { isChainAllowed } from 'utils/switchChain'
import { isiOS, isMobile } from 'utils/userAgent'

import HistoryRoulette from './components/HistoryRoulette'
import { BetType } from './types'

const unityContext = new UnityContext({
  loaderUrl: 'unity/roulette/builds.loader.js',
  dataUrl: 'unity/roulette/builds.data.unityweb',
  frameworkUrl: 'unity/roulette/builds.framework.js.unityweb',
  codeUrl: 'unity/roulette/builds.wasm.unityweb',
})

const PageWrapper = styled.div`
  max-width: 900px;
  width: 100%;
  margin-top: 2rem;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    max-width: 500px;
  `};

  ${({ theme }) => theme.mediaWidth.upToSmall`
    max-width: 400px;
    margin-top: 5rem;
  `};
`

function Roulette() {
  const { account, chainId, connector } = useActiveWeb3React()
  const chainAllowed = chainId && isChainAllowed(connector, chainId)
  const apiUrl = ROULETTE_SUBGRAPH_URL[chainAllowed ? chainId : 137]
  const { landscape, portrait } = useWindowOrientation()
  const [isDark] = useDarkModeManager()
  const [isLoaded, setIsLoaded] = useState(false)
  const [isinitialLoaded, setIsinitialLoaded] = useState(false)
  const [requestId, setRequestId] = useState<string | undefined>()
  const [progression, setProgression] = useState(0)
  const [fullScreen, setFullScreen] = useState(false)
  const [WinNumber, setWinNumber] = useState<number | null>()

  const isApple = isiOS()

  const route = useCurrentRoute()
  const currentLang = useUserLocaleManager()

  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms))

  const playerData = usePlayerData()
  const { chipsBalance } = playerData

  const rouletteContract = useRouletteContract()

  useEffect(() => {
    const getLastBet = async (id: string) => {
      const { player } = await request(
        apiUrl,
        gql`
          query GetPlayerBets($address: String!, $id: String!) {
            player(id: $address) {
              bets(where: { id: $id }) {
                winNumber
                prize
              }
            }
          }
        `,
        {
          address: account?.toLowerCase(),
          id,
        }
      )
      const _WinNumber: number | null = player?.bets[0]?.winNumber
      const prize = player?.bets[0]?.prize
      const amount = new BigNumber(prize ?? 0)
      if (_WinNumber) {
        setWinNumber(_WinNumber ?? null)
        playerData.fetchData()

        const params = {
          WinnerNumber: _WinNumber,
          CurrentBalance: getBalanceNumber(chipsBalance, 18),
          Prize: getBalanceNumber(amount, 18),
        }
        unityContext.send('BetController', 'ResolveBet', JSON.stringify(params))
      } else {
        // eslint-disable-next-line no-promise-executor-return
        await delay(2000)
        getLastBet(id)
      }
    }
    if (requestId) {
      getLastBet(requestId)
    }
  }, [account, apiUrl, requestId])

  const bet = useCallback(
    async (betInformation) => {
      const betData = JSON.parse(betInformation)
      const arrayBets = []
      for (let i = 0; i < betData.myList.length; i += 1) {
        const tempBet = betData.myList[i]
        const wins = tempBet.winningNumbers
        let { betType } = tempBet
        const max: number = wins.reduce((op: number, item: number) => {
          return op > item ? op : item
        }, 0)
        const min: number = wins.reduce((_min: number, p: number) => (p < _min ? p : _min), wins[0])
        const betAmount = new BigNumber(tempBet.value).times(DEFAULT_TOKEN_DECIMALS)

        // change bet type if is special 0 or 00
        // compare wins array include 0,1,2
        if (wins.includes(0) && wins.includes(1) && wins.includes(2)) {
          betType = BetType.Street
        }
        if (wins.includes(2) && wins.includes(3) && wins.includes(37)) {
          betType = BetType.Street
        }
        if (wins.includes(0) && wins.includes(1) && wins.includes(2) && wins.includes(3) && wins.includes(37)) {
          betType = BetType.Five
        }

        const numbers: [number, number] = [min, max]

        arrayBets.push({ amount: betAmount.toFixed(0), numbers, betType: Number(betType) })
        /*  arrayBets.push([betAmount, [min, max], betType]) */
      }

      try {
        const response = await rouletteContract?.playBet(arrayBets)?.catch((err: any) => {
          console.log(err)
          unityContext.send('BetController', 'CancelTX')
        })
        const receipt = await response?.wait()
        if (response?.hash) {
          const eventFilter = rouletteContract?.filters.NewBet()
          const events = await rouletteContract?.queryFilter(eventFilter!, receipt?.blockHash)
          const requestId = events?.[0].args.id.toString()
          unityContext.send('BetController', 'SpinRoulette')
          await delay(5000)
          playerData.fetchData()
          setRequestId(requestId)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        unityContext.send('BetController', 'CancelTX')
      }
    },
    [account, rouletteContract]
  )

  useEffect(() => {
    unityContext.on('PlayBet', bet)
    return () => {
      unityContext.removeEventListener('PlayBet')
    }
  }, [bet])

  useEffect(() => {
    unityContext.on('progress', (_progression) => {
      setProgression(_progression)
    })
    return () => {
      unityContext.removeEventListener('progress')
    }
  }, [])

  useEffect(() => {
    if (unityContext && unityContext.unityInstance) {
      unityContext.unityInstance.Quit()
    }
  }, [route])

  const setup = useCallback(() => {
    const lang = currentLang[0] === 'es-ES' ? 'es' : 'en'
    const balance: number = getBalanceNumber(chipsBalance, 18)
    const params = { Language: lang, Balance: balance, MyInitialStatus: 0, InitialStatus: 0 }
    unityContext.send('BetController', 'InitialSetUp', JSON.stringify(params))
  }, [chipsBalance, currentLang])

  useEffect(() => {
    if (progression === 1) {
      setTimeout(() => {
        setIsLoaded(true)
      }, 3000)
    }
  }, [progression])

  useEffect(() => {
    if (isLoaded && isinitialLoaded && unityContext && unityContext.unityInstance) {
      const params = {
        CurrentBalance: getBalanceNumber(chipsBalance, 18),
      }
      unityContext.send('BetController', 'UpdateBalance', JSON.stringify(params))
    }
  }, [chipsBalance, isLoaded, isinitialLoaded])

  useEffect(() => {
    if (isLoaded && !isinitialLoaded) {
      setup()
      setIsinitialLoaded(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isLoaded, isinitialLoaded])

  const changeScreen = useCallback(async (change: boolean) => {
    await delay(500)
    unityContext.setFullscreen(change)
  }, [])

  useEffect(() => {
    if (isMobile && isLoaded && isinitialLoaded && unityContext && unityContext.unityInstance) {
      if (landscape && !fullScreen) {
        setFullScreen(true)
        /* changeScreen(true) */
      }
      if (portrait && fullScreen) {
        setFullScreen(false)
        changeScreen(false)
      }
    }
  }, [changeScreen, fullScreen, isLoaded, isinitialLoaded, landscape, portrait])

  function handleOnClickFullscreen() {
    if (unityContext && unityContext.unityInstance) {
      unityContext.setFullscreen(true)
    }
  }

  return (
    <>
      <Unity unityContext={unityContext} style={isMobile ? { width: '100%' } : { width: '80%' }} />
      {landscape && !isApple && (
        <ButtonPrimary width={isMobile ? '40%' : '20%'} margin="auto" mt={3} onClick={handleOnClickFullscreen}>
          <img src={FullScreenWhite} alt="" width={24} />
          <ThemedText.Body ml="1rem" fontSize={20} color="#fff">
            <Trans>Full screen</Trans>
          </ThemedText.Body>
        </ButtonPrimary>
      )}
      {isMobile && portrait && (
        <div style={{ display: 'flex', marginTop: '1rem', alignItems: 'center' }}>
          <img src={RotateGif} alt="" width={48} />{' '}
          <ThemedText.Body ml="0.5rem">
            <Trans>Rotate device</Trans>
          </ThemedText.Body>{' '}
        </div>
      )}
      <PageWrapper>
        <HistoryRoulette winNumber={WinNumber} />
      </PageWrapper>
    </>
  )
}

export default Roulette
